import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import "./folded-line.css";

/**
 * One line of text that fades out toward a chevron when there is more; the
 * chevron opens the rest. Long text never has to be cut short at the source:
 * the reader sees a line and opens what they care about.
 *
 * The chevron shows only when the text actually overflows, measured on screen.
 */
export function FoldedLine({ className = "", children }: { className?: string; children: ReactNode }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const text = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const element = text.current;
    if (element && !open) setMore(element.scrollHeight > element.clientHeight + 1);
  }, [children, open]);
  return (
    <div className={`folded-line${className ? ` ${className}` : ""}${open ? " is-open" : more ? " has-more" : ""}`}>
      <div ref={text} className="folded-line-text">{children}</div>
      {more ? (
        <button type="button" className="folded-line-toggle" aria-expanded={open} aria-label={t(open ? "common.showLess" : "common.showMore")} onClick={() => setOpen(!open)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
        </button>
      ) : null}
    </div>
  );
}
